import React from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  IconButton,
  Divider,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RiskMatrix from './components/RiskMatrix';

const RiskView = ({ data, onBack }) => {
  if (!data) return null;

  const getRatingInfo = (likelihood, consequence) => {
    const likelihoodMap = { 'RARE': 0, 'UNLIKELY': 1, 'POSSIBLE': 2, 'LIKELY': 3, 'ALMOST CERTAIN': 4 };
    const matrix = {
      'CATASTROPHIC': ['TOLERABLE', 'HIGH', 'EXTREME', 'EXTREME', 'EXTREME'],
      'MAJOR': ['LOW', 'TOLERABLE', 'HIGH', 'EXTREME', 'EXTREME'],
      'MODERATE': ['LOW', 'LOW', 'TOLERABLE', 'HIGH', 'HIGH'],
      'MINOR': ['VERY LOW', 'LOW', 'TOLERABLE', 'TOLERABLE', 'HIGH'],
      'INSIGNIFICANT': ['VERY LOW', 'VERY LOW', 'LOW', 'TOLERABLE', 'TOLERABLE']
    };
    const colors = {
      'EXTREME': '#ef4444',
      'HIGH': '#f97316',
      'TOLERABLE': '#eab308',
      'LOW': '#84cc16',
      'VERY LOW': '#15803d'
    };
    const idx = likelihoodMap[String(likelihood).toUpperCase()];
    const consKey = String(consequence).toUpperCase();
    const label = matrix[consKey]?.[idx] || '---';
    return { label, color: colors[label] || '#94a3b8' };
  };

  const getLikelihoodLabel = (val) => {
    const map = { 1: 'RARE', 2: 'UNLIKELY', 3: 'POSSIBLE', 4: 'LIKELY', 5: 'ALMOST CERTAIN' };
    return map[val] || val;
  };

  const getConsequenceLabel = (val) => {
    const map = { 1: 'INSIGNIFICANT', 2: 'MINOR', 3: 'MODERATE', 4: 'MAJOR', 5: 'CATASTROPHIC' };
    return map[val] || val;
  };

  const SectionHeader = ({ title, color }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 3 }}>
        <Typography variant="subtitle1" sx={{ color: color, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.85rem' }}>
            {title}
        </Typography>
    </Box>
  );

  const DataField = ({ label, value, fullWidth = false }) => (
    <Grid item xs={12} md={fullWidth ? 12 : 4}>
        <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', mb: 0.5, fontWeight: '500' }}>{label}</Typography>
        <Typography sx={{ color: '#f8fafc', fontSize: '0.85rem' }}>{value || '---'}</Typography>
    </Grid>
  );

  const LongTextField = ({ label, value }) => (
    <Grid item xs={12}>
        <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', mb: 1, fontWeight: '500' }}>{label}</Typography>
        <Box sx={{ p: 2, background: 'rgba(255,255,255,0.04)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.06)' }}>
            <Typography sx={{ color: '#f1f5f9', lineHeight: 1.6, fontSize: '0.85rem' }}>{value || 'No information provided.'}</Typography>
        </Box>
    </Grid>
  );

  return (
    <Box sx={{ p: 4, maxWidth: '1200px', margin: '0 auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={onBack} sx={{ color: 'white', mr: 2, background: 'rgba(255,255,255,0.05)', '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
            <Typography variant="h4" className="gradient-text" sx={{ fontWeight: 'bold' }}>
                Risk Profile
            </Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>Assessment for Record {data.sn}</Typography>
        </Box>
      </Box>

      <Paper className="glass" sx={{ p: 5, background: 'rgba(30, 41, 59, 0.4)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.1)' }}>
        <Grid container spacing={4}>
            {/* Identification Section */}
            <Grid item xs={12}>
                <SectionHeader title="RISK IDENTIFICATION" color="#3b82f6" />
                <Divider sx={{ borderColor: 'rgba(59, 130, 246, 0.2)', mb: 3 }} />
                <Grid container spacing={3}>
                    <DataField label="Risk ID" value={data.sn} />
                    <DataField label="Date Reviewed" value={data.date_reviewed} />
                    <DataField label="Category" value={data.category} />
                    <DataField label="Institutional Unit" value={data.department_id} />
                    {/* Risk Owner moved to Controls & Treatment Section */}
                    <Grid item xs={12} md={4} />
                    <LongTextField label="Risk Description" value={data.risk_description} />
                    <LongTextField label="KRA at Risk" value={data.kra_at_risk} />
                    <LongTextField label="Root Causes" value={data.causes} />
                    <LongTextField label="Potential Consequences" value={data.consequence} />
                </Grid>
            </Grid>

            {/* Inherent Assessment Section */}
            <Grid item xs={12} sx={{ mb: 4 }}>
                <Grid container spacing={4} alignItems="stretch">
                    <Grid item xs={12} md={6}>
                        <SectionHeader title="RISK ASSESSMENT (INHERENT)" color="#3b82f6" />
                        <Divider sx={{ borderColor: 'rgba(59, 130, 246, 0.2)', mb: 3 }} />
                        <Box sx={{ p: 3, background: 'rgba(59, 130, 246, 0.05)', borderRadius: '16px', border: '1px solid rgba(59, 130, 246, 0.1)', height: 'calc(100% - 60px)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 6, alignItems: 'center' }}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography sx={{ color: '#64748b', fontSize: '0.55rem', letterSpacing: '0.5px' }}>LIKELIHOOD</Typography>
                                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem' }}>{getLikelihoodLabel(data.inherent_likelihood)}</Typography>
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography sx={{ color: '#64748b', fontSize: '0.55rem', letterSpacing: '0.5px' }}>CONSEQUENCE</Typography>
                                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem' }}>{getConsequenceLabel(data.inherent_consequence)}</Typography>
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography sx={{ color: getRatingInfo(data.inherent_likelihood, data.inherent_consequence).color, fontSize: '0.55rem', fontWeight: 'bold', letterSpacing: '1px' }}>RATING</Typography>
                                    <Typography sx={{ color: getRatingInfo(data.inherent_likelihood, data.inherent_consequence).color, fontWeight: '900', fontSize: '0.85rem' }}>{getRatingInfo(data.inherent_likelihood, data.inherent_consequence).label}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper className="glass" sx={{ p: 4, background: 'rgba(15, 23, 42, 0.2)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                <RiskMatrix selectedLikelihood={data.inherent_likelihood} selectedConsequence={data.inherent_consequence} title="Inherent Risk Matrix" />
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Grid>

            {/* Residual Assessment Section */}
            <Grid item xs={12} sx={{ mb: 4 }}>
                <Grid container spacing={4} alignItems="stretch">
                    <Grid item xs={12} md={6}>
                        <SectionHeader title="RISK ASSESSMENT (RESIDUAL)" color="#3b82f6" />
                        <Divider sx={{ borderColor: 'rgba(59, 130, 246, 0.2)', mb: 3 }} />
                        <Box sx={{ p: 3, background: 'rgba(59, 130, 246, 0.05)', borderRadius: '16px', border: '1px solid rgba(59, 130, 246, 0.1)', height: 'calc(100% - 60px)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 6, alignItems: 'center' }}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography sx={{ color: '#64748b', fontSize: '0.55rem', letterSpacing: '0.5px' }}>LIKELIHOOD</Typography>
                                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem' }}>{getLikelihoodLabel(data.residual_likelihood)}</Typography>
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography sx={{ color: '#64748b', fontSize: '0.55rem', letterSpacing: '0.5px' }}>CONSEQUENCE</Typography>
                                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem' }}>{getConsequenceLabel(data.residual_consequence)}</Typography>
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography sx={{ color: getRatingInfo(data.residual_likelihood, data.residual_consequence).color, fontSize: '0.55rem', fontWeight: 'bold', letterSpacing: '1px' }}>RATING</Typography>
                                    <Typography sx={{ color: getRatingInfo(data.residual_likelihood, data.residual_consequence).color, fontWeight: '900', fontSize: '0.85rem' }}>{getRatingInfo(data.residual_likelihood, data.residual_consequence).label}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper className="glass" sx={{ p: 4, background: 'rgba(15, 23, 42, 0.2)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                <RiskMatrix selectedLikelihood={data.residual_likelihood} selectedConsequence={data.residual_consequence} title="Residual Risk Matrix" />
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Grid>
            {/* Paired Treatment & Monitoring Section */}
            <Grid item xs={12} sx={{ mt: 2 }}>
                <Grid container spacing={4} alignItems="stretch">
                    <Grid item xs={12} md={6}>
                        <SectionHeader title="CONTROLS & TREATMENT" color="#3b82f6" />
                        <Divider sx={{ borderColor: 'rgba(59, 130, 246, 0.2)', mb: 3 }} />
                        <Grid container spacing={3}>
                            <LongTextField label="Existing Controls" value={data.existing_controls} />
                            <DataField label="Control Adequacy" value={data.control_effectiveness} />
                            <DataField label="Action" value={data.mitigation_strategy} />
                            <LongTextField label="Action Plan" value={data.action_treatment} />
                            <DataField label="Risk Owner" value={data.owner} />
                            <DataField label="TARGET RESOLUTION" value={data.resolved_by} />
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <SectionHeader title="RISK MONITORING & REVIEW" color="#3b82f6" />
                        <Divider sx={{ borderColor: 'rgba(59, 130, 246, 0.2)', mb: 3 }} />
                        <Grid container spacing={3}>
                            <DataField label="Method" value={data.method} />
                            <DataField label="Current Status" value={data.status} />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default RiskView;
